/**
 * API de Suscripciones
 * Funciones para interactuar con el backend de suscripciones usando RPC de Supabase
 */

import { supabase } from '../lib/supabase';

/**
 * Obtiene la suscripción activa del usuario con información completa
 * @param {string} userId - ID del usuario
 * @returns {Promise<Object>} - Datos de la suscripción o null
 */
export const getUserActiveSubscription = async (userId) => {
  try {
    const { data, error } = await supabase
      .rpc('get_user_active_subscription', { 
        p_usuario_id: userId 
      });

    if (error) {
      console.error('Error obteniendo suscripción activa:', error);
      throw error;
    }

    // Retornar primer resultado o null
    return data && data.length > 0 ? data[0] : null;
  } catch (error) {
    console.error('Error en getUserActiveSubscription:', error);
    throw error;
  }
};

/**
 * Obtiene información detallada de la suscripción con historial de pagos
 * @param {string} userId - ID del usuario
 * @param {boolean} includePayments - Incluir historial de pagos
 * @returns {Promise<Array>} - Array con datos de la suscripción
 */
export const getUserSubscriptionDetails = async (userId, includePayments = false) => {
  try {
    const { data, error } = await supabase
      .rpc('get_user_subscription_details', { 
        p_usuario_id: userId,
        p_include_payments: includePayments
      });

    if (error) {
      console.error('Error obteniendo detalles de suscripción:', error);
      throw error;
    }

    return data || [];
  } catch (error) {
    console.error('Error en getUserSubscriptionDetails:', error);
    throw error;
  }
};

/**
 * Obtiene el historial completo de pagos de una suscripción
 * @param {number} subscriptionId - ID de la suscripción
 * @returns {Promise<Array>} - Array de pagos
 */
export const getSubscriptionPayments = async (subscriptionId) => {
  try {
    const { data, error } = await supabase
      .rpc('get_subscription_payments', { 
        p_suscripcion_id: subscriptionId 
      });

    if (error) {
      console.error('Error obteniendo pagos de suscripción:', error);
      throw error;
    }

    return data || [];
  } catch (error) {
    console.error('Error en getSubscriptionPayments:', error);
    throw error;
  }
};

/**
 * Obtiene suscripción con pagos incluidos (wrapper para uso común)
 * @param {string} userId - ID del usuario
 * @returns {Promise<Object>} - Datos completos de la suscripción
 */
export const getSubscriptionWithPayments = async (userId) => {
  try {
    // Obtener suscripción activa
    const subscription = await getUserActiveSubscription(userId);
    
    if (!subscription) {
      return null;
    }

    // Obtener pagos si hay suscripción
    const payments = await getSubscriptionPayments(subscription.id);

    // Combinar datos
    return {
      ...subscription,
      pagos_suscripciones: payments
    };
  } catch (error) {
    console.error('Error en getSubscriptionWithPayments:', error);
    throw error;
  }
};

/**
 * Verifica si un usuario tiene suscripción activa
 * @param {string} userId - ID del usuario
 * @returns {Promise<boolean>} - True si tiene suscripción activa
 */
export const hasActiveSubscription = async (userId) => {
  try {
    const subscription = await getUserActiveSubscription(userId);
    return subscription !== null;
  } catch (error) {
    console.error('Error verificando suscripción activa:', error);
    return false;
  }
};

/**
 * Obtiene el estado de la suscripción para mostrar en UI
 * @param {string} userId - ID del usuario
 * @returns {Promise<Object>} - Estado formateado para UI
 */
export const getSubscriptionStatus = async (userId) => {
  try {
    const subscription = await getUserActiveSubscription(userId);
    
    if (!subscription) {
      return {
        hasSubscription: false,
        status: 'none',
        message: 'Sin suscripción'
      };
    }

    return {
      hasSubscription: true,
      status: subscription.estado,
      subscription: subscription,
      // Información útil para la UI
      planName: subscription.plan_nombre,
      nextPayment: subscription.proximo_pago,
      amount: subscription.plan_precio_promo || subscription.plan_precio,
      currency: subscription.plan_moneda,
      isActive: ['authorized', 'active'].includes(subscription.estado),
      isPending: subscription.estado === 'pending'
    };
  } catch (error) {
    console.error('Error obteniendo estado de suscripción:', error);
    return {
      hasSubscription: false,
      status: 'error',
      message: 'Error al verificar suscripción'
    };
  }
};

/**
 * Formatea los datos de suscripción para el componente SubscriptionStatus
 * @param {Object} subscription - Datos crudos de la suscripción
 * @returns {Object} - Datos formateados para el componente
 */
export const formatSubscriptionForComponent = (subscription) => {
  if (!subscription) return null;

  return {
    id: subscription.id,
    usuario_id: subscription.usuario_id,
    plan_id: subscription.plan_id,
    mp_preapproval_id: subscription.mp_preapproval_id,
    estado: subscription.estado,
    fecha_inicio: subscription.fecha_inicio,
    fecha_fin: subscription.fecha_fin,
    fecha_cancelacion: subscription.fecha_cancelacion,
    proximo_pago: subscription.proximo_pago,
    monto: subscription.monto,
    moneda: subscription.moneda,
    frecuencia: subscription.frecuencia,
    intentos_pago: subscription.intentos_pago,
    ultimo_error_pago: subscription.ultimo_error_pago,
    creado_en: subscription.creado_en,
    actualizado_en: subscription.actualizado_en,
    datos_pago: subscription.datos_pago,
    
    // Mantener compatibilidad con campos existentes y agregar plan_mp_plan_link
    plan_nombre: subscription.plan_nombre,
    plan_moneda: subscription.plan_moneda,
    plan_precio: subscription.plan_precio,
    plan_precio_promo: subscription.plan_precio_promo,
    plan_frecuencia: subscription.plan_frecuencia,
    plan_trial_days: subscription.plan_trial_days,
    plan_descripcion: subscription.plan_descripcion,
    plan_es_destacado: subscription.plan_es_destacado,
    plan_mp_plan_link: subscription.plan_mp_plan_link,
    
    // Pagos (si se incluyen)
    pagos_suscripciones: subscription.pagos_suscripciones || []
  };
};

export default {
  getUserActiveSubscription,
  getUserSubscriptionDetails,
  getSubscriptionPayments,
  getSubscriptionWithPayments,
  hasActiveSubscription,
  getSubscriptionStatus,
  formatSubscriptionForComponent
};
